/**
 * Sync the Yagi Automatic Discount Helper price into the Recharge Subscription
 * Widget's "One-time purchase" option.
 *
 * Why this is needed: Yagi never rewrites product.price/compare_at_price (so the
 * automatic discount isn't double-applied at checkout) - it only renders its own
 * discounted price into a ".yagi-discounted-price" element via its app block.
 * Recharge's widget (a <recharge-subscription-widget> web component rendered in a
 * shadow root) has no way to know about that automatic discount, so its one-time
 * price shows the native Shopify price instead. This patch is display-only: the
 * actual checkout total is already correct today via the Shopify discount
 * Function, regardless of what either app shows on the PDP.
 *
 * Confirmed against the live shadow DOM (see the ::part() names already
 * referenced in assets/styles.css):
 *   .rc-selection__onetime [part="rc-purchase-option__price"]  -> one-time price
 * Yagi's own rendered text (".yagi-discounted-price") is used as-is so this
 * automatically matches the shop's existing currency/locale formatting.
 */
(function () {
  var knownWidgets = [];
  var yagiPriceWatchBound = false;

  function getYagiDiscountedPriceText() {
    var el = document.querySelector('.yagi-discounted-price');
    if (!el) return null; // no active Yagi discount right now
    var text = el.textContent.replace(/\s+/g, ' ').trim();
    return text || null;
  }

  // Writes text into an existing element WITHOUT replacing its Text node.
  // `el.textContent = text` destroys and replaces all of el's children with a
  // brand new Text node - if Recharge's renderer (Lit) holds an internal
  // reference to the *original* Text node to update it efficiently on future
  // re-renders, that reference is silently orphaned by the reassignment, and
  // Recharge never successfully touches this price again for the rest of the
  // page view (confirmed live: after patching this way once, the widget stops
  // updating its one-time price on every subsequent variant switch, even
  // though it updates correctly on its own when nothing has patched it yet).
  // Mutating the existing node's `.data` in place preserves that reference and
  // leaves Recharge free to keep updating the node normally afterward.
  function setTextPreservingNode(el, text) {
    var textNode = null;
    for (var i = 0; i < el.childNodes.length; i++) {
      if (el.childNodes[i].nodeType === Node.TEXT_NODE) {
        textNode = el.childNodes[i];
        break;
      }
    }
    if (textNode) {
      textNode.data = text;
    } else {
      el.appendChild(document.createTextNode(text));
    }
  }

  function syncOneTimePrice(widget) {
    if (!widget || !widget.shadowRoot) return;

    var text = getYagiDiscountedPriceText();
    if (!text) return; // leave Recharge's native one-time price untouched

    var onetimePriceEl = widget.shadowRoot.querySelector(
      '.rc-selection__onetime [part="rc-purchase-option__price"]'
    );
    if (!onetimePriceEl) return;

    if (onetimePriceEl.textContent.trim() !== text) {
      setTextPreservingNode(onetimePriceEl, text);
    }
  }

  function syncAllWidgets() {
    knownWidgets.forEach(syncOneTimePrice);
  }

  function watchWidget(widget) {
    if (widget.yagiRechargeSyncBound) return;
    widget.yagiRechargeSyncBound = true;
    knownWidgets.push(widget);

    syncOneTimePrice(widget);

    if (widget.shadowRoot) {
      // Recharge re-renders its shadow DOM internally (e.g. after refetching
      // pricing), which would otherwise overwrite our patch - watch its own
      // shadow root (a top-level observer on document can't see into it) and
      // re-apply whenever that happens.
      new MutationObserver(function () {
        syncOneTimePrice(widget);
      }).observe(widget.shadowRoot, { childList: true, subtree: true, characterData: true });
    }
  }

  function watchYagiPriceEl() {
    if (yagiPriceWatchBound) return;

    // Yagi updates its own price element's *text* in place on variant change
    // (it doesn't add/remove nodes), which the childList-only observer below
    // won't catch - watch it directly so a variant switch that changes Yagi's
    // price also gets pushed into the widget once it settles on the new value.
    var yagiPriceEl = document.querySelector('.yagi-discounted-price');
    if (!yagiPriceEl) return; // not rendered yet (or no active discount) - try again on the next scan()

    yagiPriceWatchBound = true;
    new MutationObserver(syncAllWidgets).observe(yagiPriceEl, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  // Yagi's app block can only be placed as a top-level block in the section
  // (Shopify renders app blocks as siblings of the other blocks), so it can't
  // be positioned inside the buy box's price row from the theme editor. Move
  // its wrapper into the price slot instead, right after the native price
  // element - which Yagi itself hides via its own
  // `[id^="price"] { display: none !important }` rule, so the two never show
  // at once.
  //
  // The whole app-block wrapper is moved rather than just its inner
  // .yagi-price-container, so that Yagi's own block-level innerHTML
  // replacement (used for variant changes on some themes) still targets an
  // element sitting in the right place. Yagi queries its price nodes at
  // document level, so relocating them doesn't break its updates, and moving
  // an already-executed <script> node does not re-run it.
  function placeYagiBlockInPriceSlot() {
    var yagiBlock = document.querySelector(
      "[id*='yagi_automatic_discount_helper_auto_discounted_price_product'], [data-block-handle='auto_discounted_price_product']"
    );
    // Anchor on the native price element only while it still carries the id
    // Yagi hides it by - without that id it stays visible, and moving Yagi's
    // block next to it would show two prices side by side.
    var priceSlot = document.querySelector('.product_price[id^="price-"]');
    if (!yagiBlock || !priceSlot || !priceSlot.parentNode) return;

    // Already in place - don't move it again (this also stops the observer
    // below from ping-ponging on the mutation our own move produces).
    if (priceSlot.nextElementSibling === yagiBlock) return;

    priceSlot.parentNode.insertBefore(yagiBlock, priceSlot.nextSibling);
  }

  function scan() {
    document.querySelectorAll('recharge-subscription-widget').forEach(watchWidget);
    watchYagiPriceEl();
    placeYagiBlockInPriceSlot();
  }

  // Both the Recharge widget and Yagi's price block are inserted/updated
  // asynchronously (app blocks, variant changes), so keep watching for either.
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('DOMContentLoaded', scan);
  scan();
})();
